import { libraryQuery } from '@/components/library/api'
import { useQuery } from '@tanstack/react-query'
import { formatPercentBp, MONTHLY_CONVERSIONS } from '@hcd/domain'
import { Info } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { can } from '@/lib/roles'
import type { StaffUser } from '@/lib/session'
import { settingsQuery, type ContractType, type OnboardingItem, type VatRate } from '@/lib/settings'
import {
  FormField,
  MoneyInput,
  NativeSelect,
  PercentInput,
  SettingsCard,
  TextInput,
  ToggleRow,
} from './form-kit'
import { ResourceManager } from './resource-manager'
import { SectionEditor } from './section-editor'

export function PageTitle({ title, description }: { title: string; description: string }) {
  return (
    <header className="mb-5">
      <h1 className="text-2xl font-semibold">{title}</h1>
      <p className="mt-1 max-w-[65ch] text-[15px] text-muted-foreground">{description}</p>
    </header>
  )
}

// ---- Company ------------------------------------------------------------
export function CompanyPage({ me }: { me: StaffUser }) {
  return (
    <>
      <PageTitle
        title="Company details"
        description="Shown on proposals, contracts and the evidence certificate."
      />
      <SectionEditor section="company" canEdit={can.manageSettings(me)}>
        {({ draft, set, errors, disabled }) => (
          <SettingsCard title="Business">
            <div className="grid gap-5 sm:grid-cols-2">
              <FormField label="Trading name" error={errors.tradingName}>
                {(p) => (
                  <TextInput
                    {...p}
                    value={draft.tradingName}
                    disabled={disabled}
                    onChange={(e) => set('tradingName', e.target.value)}
                  />
                )}
              </FormField>
              <FormField label="Legal name" error={errors.legalName}>
                {(p) => (
                  <TextInput
                    {...p}
                    value={draft.legalName}
                    disabled={disabled}
                    onChange={(e) => set('legalName', e.target.value)}
                  />
                )}
              </FormField>
              <FormField label="Company number" error={errors.companyNumber}>
                {(p) => (
                  <TextInput
                    {...p}
                    value={draft.companyNumber}
                    disabled={disabled}
                    onChange={(e) => set('companyNumber', e.target.value)}
                  />
                )}
              </FormField>
              <FormField
                label="VAT number"
                hint="Leave blank if not VAT registered."
                error={errors.vatNumber}
              >
                {(p) => (
                  <TextInput
                    {...p}
                    value={draft.vatNumber}
                    disabled={disabled}
                    onChange={(e) => set('vatNumber', e.target.value)}
                  />
                )}
              </FormField>
              <FormField label="Phone" error={errors.phone}>
                {(p) => (
                  <TextInput
                    {...p}
                    type="tel"
                    inputMode="tel"
                    value={draft.phone}
                    disabled={disabled}
                    onChange={(e) => set('phone', e.target.value)}
                  />
                )}
              </FormField>
              <FormField
                label="Email"
                error={errors.email && 'Enter a valid email address, or leave it blank.'}
              >
                {(p) => (
                  <TextInput
                    {...p}
                    type="email"
                    inputMode="email"
                    value={draft.email}
                    disabled={disabled}
                    onChange={(e) => set('email', e.target.value)}
                  />
                )}
              </FormField>
              <FormField
                label="Website"
                hint="Include https://"
                error={errors.website && 'Enter a full web address, starting https://'}
                className="sm:col-span-2"
              >
                {(p) => (
                  <TextInput
                    {...p}
                    type="url"
                    inputMode="url"
                    value={draft.website}
                    disabled={disabled}
                    onChange={(e) => set('website', e.target.value)}
                  />
                )}
              </FormField>
              <FormField
                label="Registered office address"
                error={errors.registeredAddress}
                className="sm:col-span-2"
              >
                {(p) => (
                  <Textarea
                    {...p}
                    rows={3}
                    value={draft.registeredAddress}
                    disabled={disabled}
                    onChange={(e) => set('registeredAddress', e.target.value)}
                    className="text-base md:text-[15px]"
                  />
                )}
              </FormField>
            </div>
          </SettingsCard>
        )}
      </SectionEditor>
    </>
  )
}

// ---- Tax ----------------------------------------------------------------
export function TaxPage({ me }: { me: StaffUser }) {
  const admin = can.manageSettings(me)
  return (
    <>
      <PageTitle
        title="Tax and VAT"
        description="VAT is always worked out by the server, in whole pence, and saved with each quote."
      />
      <div className="grid gap-5">
        <SectionEditor section="tax" canEdit={admin}>
          {({ draft, set, disabled }) => (
            <SettingsCard title="VAT">
              <ToggleRow
                label="HCD is VAT registered"
                description="When off, quotes show no VAT."
                checked={draft.vatRegistered}
                disabled={disabled}
                onChange={(v) => set('vatRegistered', v)}
              />
              <fieldset className="grid gap-2" disabled={disabled}>
                <legend className="mb-1 text-sm font-medium">How VAT is rounded</legend>
                {(
                  [
                    [
                      'per_line',
                      'On each line',
                      'VAT is rounded to the penny on every line, then added up.',
                    ],
                    [
                      'per_total',
                      'On the total',
                      'VAT is worked out once on the total of each VAT rate.',
                    ],
                  ] as const
                ).map(([value, label, hint]) => (
                  <label
                    key={value}
                    className="flex cursor-pointer items-start gap-3 rounded-lg border p-3 has-checked:border-primary has-checked:bg-accent/40"
                  >
                    <input
                      type="radio"
                      name="rounding"
                      className="mt-1 size-4 accent-(--primary)"
                      checked={draft.rounding === value}
                      onChange={() => set('rounding', value)}
                    />
                    <span>
                      <span className="block text-sm font-medium">{label}</span>
                      <span className="block text-[13px] text-muted-foreground">{hint}</span>
                    </span>
                  </label>
                ))}
              </fieldset>
            </SettingsCard>
          )}
        </SectionEditor>

        <ResourceManager<VatRate>
          path="vat-rates"
          noun="VAT rate"
          title="VAT rates"
          description="Every price book item uses one of these. The default is chosen automatically for new items."
          canEdit={admin}
          empty="No VAT rates yet."
          blank={{ name: '', code: '', rateBp: 2000, isDefault: false }}
          archiveBlocked={(v) =>
            v.isDefault ? 'Make another rate the default before archiving this one.' : null
          }
          row={(v) => ({
            primary: <span>{v.name}</span>,
            secondary: (
              <span className="tabular">
                {formatPercentBp(v.rateBp)} · code {v.code}
              </span>
            ),
            badges: v.isDefault ? <Badge>Default</Badge> : null,
          })}
          toInput={(d) => ({
            name: d.name,
            code: d.code,
            rateBp: d.rateBp,
            isDefault: d.isDefault,
          })}
          form={(d, set) => {
            const errors: Record<string, string> = {}
            if (!d.name?.trim()) errors.name = 'Enter a name.'
            if (!/^[A-Z0-9]{1,10}$/.test(d.code ?? ''))
              errors.code = 'Use 1–10 capital letters or numbers, for example S.'
            if (d.rateBp == null) errors.rateBp = 'Enter a rate between 0 and 100.'
            return {
              errors,
              fields: (
                <>
                  <FormField label="Name" error={errors.name}>
                    {(p) => (
                      <TextInput
                        {...p}
                        autoFocus
                        value={d.name ?? ''}
                        onChange={(e) => set({ name: e.target.value })}
                      />
                    )}
                  </FormField>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="Code" hint="Short code for exports" error={errors.code}>
                      {(p) => (
                        <TextInput
                          {...p}
                          value={d.code ?? ''}
                          onChange={(e) => set({ code: e.target.value.toUpperCase() })}
                        />
                      )}
                    </FormField>
                    <FormField label="Rate" error={errors.rateBp}>
                      {(p) => (
                        <PercentInput
                          {...p}
                          valueBp={d.rateBp ?? null}
                          onChangeBp={(bp) => set({ rateBp: bp ?? undefined })}
                        />
                      )}
                    </FormField>
                  </div>
                  <ToggleRow
                    label="Default rate"
                    description="Used for new price book items."
                    checked={!!d.isDefault}
                    onChange={(v) => set({ isDefault: v })}
                  />
                </>
              ),
            }
          }}
        />
      </div>
    </>
  )
}

// ---- Quotes -------------------------------------------------------------
const CONVERSION_LABEL: Record<(typeof MONTHLY_CONVERSIONS)[number], string> = {
  '52_12': '52 weeks ÷ 12 months (4.33 weeks, exact)',
  '4_33': '4.33 weeks per month',
  '4': '4 weeks per month',
}

export function QuotesPage({ me }: { me: StaffUser }) {
  return (
    <>
      <PageTitle
        title="Quotes"
        description="Defaults for every new quote. Each quote can still be changed before it is approved."
      />
      <SectionEditor section="quotes" canEdit={can.manageSettings(me)}>
        {({ draft, set, errors, disabled }) => (
          <div className="grid gap-5">
            <SettingsCard title="Validity and numbering">
              <div className="grid gap-5 sm:grid-cols-3">
                <FormField
                  label="Valid for (days)"
                  error={errors.validityDays && 'Enter a number of days from 1 to 365.'}
                >
                  {(p) => (
                    <TextInput
                      {...p}
                      inputMode="numeric"
                      value={String(draft.validityDays)}
                      disabled={disabled}
                      onChange={(e) =>
                        set('validityDays', Number(e.target.value.replace(/\D/g, '')) || 0)
                      }
                    />
                  )}
                </FormField>
                <FormField
                  label="Reference prefix"
                  error={errors.referencePrefix}
                  hint={`Next quote: ${draft.referencePrefix}${draft.nextNumber}`}
                >
                  {(p) => (
                    <TextInput
                      {...p}
                      value={draft.referencePrefix}
                      disabled={disabled}
                      onChange={(e) => set('referencePrefix', e.target.value.toUpperCase())}
                    />
                  )}
                </FormField>
                <FormField label="Next number" error={errors.nextNumber && 'Enter a whole number.'}>
                  {(p) => (
                    <TextInput
                      {...p}
                      inputMode="numeric"
                      value={String(draft.nextNumber)}
                      disabled={disabled}
                      onChange={(e) =>
                        set('nextNumber', Number(e.target.value.replace(/\D/g, '')) || 0)
                      }
                    />
                  )}
                </FormField>
              </div>
            </SettingsCard>
            <SettingsCard
              title="Recurring prices"
              description="Used when a weekly or fortnightly price is shown as a monthly figure."
            >
              <FormField label="Monthly conversion">
                {(p) => (
                  <NativeSelect
                    {...p}
                    value={draft.monthlyConversion}
                    disabled={disabled}
                    onChange={(e) =>
                      set('monthlyConversion', e.target.value as typeof draft.monthlyConversion)
                    }
                  >
                    {MONTHLY_CONVERSIONS.map((c) => (
                      <option key={c} value={c}>
                        {CONVERSION_LABEL[c]}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </FormField>
              <ToggleRow
                label="Show the monthly equivalent to clients"
                description="Adds a monthly figure beside weekly and fortnightly prices on the proposal."
                checked={draft.showMonthlyEquivalentToClient}
                disabled={disabled}
                onChange={(v) => set('showMonthlyEquivalentToClient', v)}
              />
            </SettingsCard>
            <SettingsCard
              title="Payment terms"
              description="Printed on every proposal unless changed on the quote."
            >
              <FormField label="Payment terms" error={errors.paymentTerms}>
                {(p) => (
                  <Textarea
                    {...p}
                    rows={4}
                    value={draft.paymentTerms}
                    disabled={disabled}
                    onChange={(e) => set('paymentTerms', e.target.value)}
                    className="text-base md:text-[15px]"
                  />
                )}
              </FormField>
            </SettingsCard>
          </div>
        )}
      </SectionEditor>
    </>
  )
}

// ---- Approvals ----------------------------------------------------------
export function ApprovalsPage({ me }: { me: StaffUser }) {
  return (
    <>
      <PageTitle
        title="Approvals"
        description="Decide which quotes must be approved before they reach a client. Quotes prepared by Hermes always need a person to approve them."
      />
      <SectionEditor section="approvals" canEdit={can.manageSettings(me)}>
        {({ draft, set, disabled }) => (
          <div className="grid gap-5">
            <SettingsCard title="When is approval needed?">
              <fieldset className="grid gap-2" disabled={disabled}>
                <legend className="sr-only">Approval rule</legend>
                {(
                  [
                    ['always', 'Every quote', 'Nothing is sent until an approver has checked it.'],
                    [
                      'thresholds',
                      'Only some quotes',
                      'Only quotes that meet one of the conditions below need approval.',
                    ],
                    [
                      'never',
                      'No approval',
                      'Anyone who can send quotes can send without approval. Hermes still needs approval.',
                    ],
                  ] as const
                ).map(([value, label, hint]) => (
                  <label
                    key={value}
                    className="flex cursor-pointer items-start gap-3 rounded-lg border p-3 has-checked:border-primary has-checked:bg-accent/40"
                  >
                    <input
                      type="radio"
                      name="mode"
                      className="mt-1 size-4 accent-(--primary)"
                      checked={draft.mode === value}
                      onChange={() => set('mode', value)}
                    />
                    <span>
                      <span className="block text-sm font-medium">{label}</span>
                      <span className="block text-[13px] text-muted-foreground">{hint}</span>
                    </span>
                  </label>
                ))}
              </fieldset>
              {draft.mode === 'thresholds' && (
                <div className="grid gap-5 rounded-lg bg-muted p-4 sm:grid-cols-3">
                  <FormField
                    label="Value over"
                    hint="Annual value excluding VAT. £0 turns this off."
                  >
                    {(p) => (
                      <MoneyInput
                        {...p}
                        valuePence={draft.valueAbovePence}
                        disabled={disabled}
                        onChangePence={(v) => v !== null && set('valueAbovePence', v)}
                      />
                    )}
                  </FormField>
                  <FormField label="Margin under" hint="0% turns this off.">
                    {(p) => (
                      <PercentInput
                        {...p}
                        valueBp={draft.marginBelowBp}
                        disabled={disabled}
                        onChangeBp={(v) => v !== null && set('marginBelowBp', v)}
                      />
                    )}
                  </FormField>
                  <FormField label="Discount over" hint="0% turns this off.">
                    {(p) => (
                      <PercentInput
                        {...p}
                        valueBp={draft.discountAboveBp}
                        disabled={disabled}
                        onChangeBp={(v) => v !== null && set('discountAboveBp', v)}
                      />
                    )}
                  </FormField>
                </div>
              )}
            </SettingsCard>
            <SettingsCard title="Who approves">
              <fieldset className="grid gap-2" disabled={disabled}>
                <legend className="mb-1 text-sm font-medium">Roles that can approve</legend>
                {(
                  [
                    ['owner', 'Owners'],
                    ['commercial_manager', 'Commercial managers'],
                  ] as const
                ).map(([role, label]) => (
                  <label key={role} className="flex items-center gap-3 text-sm">
                    <input
                      type="checkbox"
                      className="size-4 accent-(--primary)"
                      checked={draft.approverRoles.includes(role)}
                      onChange={(e) =>
                        set(
                          'approverRoles',
                          e.target.checked
                            ? [...draft.approverRoles, role]
                            : draft.approverRoles.filter((r) => r !== role),
                        )
                      }
                    />
                    {label}
                  </label>
                ))}
                {draft.approverRoles.length === 0 && (
                  <p className="text-[13px] font-medium text-destructive">
                    Choose at least one role.
                  </p>
                )}
              </fieldset>
              <ToggleRow
                label="Allow people to approve their own quotes"
                description="Off means a second person must always check."
                checked={draft.allowSelfApproval}
                disabled={disabled}
                onChange={(v) => set('allowSelfApproval', v)}
              />
            </SettingsCard>
          </div>
        )}
      </SectionEditor>
    </>
  )
}

// ---- Contracts ----------------------------------------------------------
export function ContractsPage({ me }: { me: StaffUser }) {
  return (
    <>
      <PageTitle
        title="Contract types"
        description="The agreements HCD issues. Templates for each type are added in the documents stage."
      />
      <ResourceManager<ContractType>
        path="contract-types"
        noun="contract type"
        title="Contract types"
        canEdit={can.manageSettings(me)}
        empty="No contract types yet."
        blank={{ name: '', description: '', requiresCountersignature: true }}
        row={(c) => ({
          primary: c.name,
          secondary: c.description,
          badges: c.requiresCountersignature ? (
            <Badge variant="secondary">HCD countersigns</Badge>
          ) : null,
        })}
        toInput={(d) => ({
          name: d.name,
          description: d.description,
          requiresCountersignature: d.requiresCountersignature,
        })}
        form={(d, set) => {
          const errors: Record<string, string> = {}
          if (!d.name?.trim()) errors.name = 'Enter a name.'
          return {
            errors,
            fields: (
              <>
                <FormField label="Name" error={errors.name}>
                  {(p) => (
                    <TextInput
                      {...p}
                      autoFocus
                      value={d.name ?? ''}
                      onChange={(e) => set({ name: e.target.value })}
                    />
                  )}
                </FormField>
                <FormField label="Description">
                  {(p) => (
                    <Textarea
                      {...p}
                      rows={3}
                      value={d.description ?? ''}
                      onChange={(e) => set({ description: e.target.value })}
                      className="text-base md:text-[15px]"
                    />
                  )}
                </FormField>
                <ToggleRow
                  label="HCD countersigns"
                  description="HCD signs after the client before the contract is complete."
                  checked={!!d.requiresCountersignature}
                  onChange={(v) => set({ requiresCountersignature: v })}
                />
              </>
            ),
          }
        }}
      />
    </>
  )
}

// ---- Onboarding ---------------------------------------------------------
const KIND_LABEL: Record<OnboardingItem['kind'], string> = {
  sign: 'Sign a document',
  form: 'Fill in a form',
  acknowledge: 'Read and confirm',
  upload: 'Upload a document',
}

export function OnboardingPage({ me }: { me: StaffUser }) {
  const library = useQuery(libraryQuery)
  const docs = (library.data ?? []).filter((d) => d.active && d.current)
  return (
    <>
      <PageTitle
        title="Starter pack"
        description="What every new starter is asked to do. You can add or remove items for an individual starter when you send their pack."
      />
      <ResourceManager<OnboardingItem>
        path="onboarding-items"
        noun="item"
        title="Items in the starter pack"
        description="Shown to the starter in this order."
        canEdit={can.managePeople(me)}
        empty="No items yet."
        blank={{
          name: '',
          kind: 'acknowledge',
          description: '',
          required: true,
          documentId: null,
          form: null,
        }}
        row={(i) => ({
          primary: i.name,
          secondary: `${KIND_LABEL[i.kind]}${i.description ? `: ${i.description}` : ''}`,
          badges: i.required ? null : <Badge variant="outline">Optional</Badge>,
        })}
        toInput={(d) => ({
          name: d.name,
          kind: d.kind,
          description: d.description,
          required: d.required,
          sort: d.sort,
          documentId: d.kind === 'sign' || d.kind === 'acknowledge' ? (d.documentId ?? null) : null,
          form: d.kind === 'form' ? (d.form ?? 'starter_details') : null,
        })}
        form={(d, set) => {
          const errors: Record<string, string> = {}
          if (!d.name?.trim()) errors.name = 'Enter a name.'
          return {
            errors,
            fields: (
              <>
                <FormField label="Name" error={errors.name}>
                  {(p) => (
                    <TextInput
                      {...p}
                      autoFocus
                      value={d.name ?? ''}
                      onChange={(e) => set({ name: e.target.value })}
                    />
                  )}
                </FormField>
                <div className="grid grid-cols-[1fr_7rem] gap-4">
                  <FormField label="What the starter does">
                    {(p) => (
                      <NativeSelect
                        {...p}
                        value={d.kind}
                        onChange={(e) => set({ kind: e.target.value as OnboardingItem['kind'] })}
                      >
                        {Object.entries(KIND_LABEL).map(([k, l]) => (
                          <option key={k} value={k}>
                            {l}
                          </option>
                        ))}
                      </NativeSelect>
                    )}
                  </FormField>
                  <FormField label="Order">
                    {(p) => (
                      <TextInput
                        {...p}
                        inputMode="numeric"
                        value={String(d.sort ?? 0)}
                        onChange={(e) =>
                          set({ sort: Number(e.target.value.replace(/\D/g, '')) || 0 })
                        }
                      />
                    )}
                  </FormField>
                </div>
                <FormField label="Instructions for the starter">
                  {(p) => (
                    <Textarea
                      {...p}
                      rows={3}
                      value={d.description ?? ''}
                      onChange={(e) => set({ description: e.target.value })}
                      className="text-base md:text-[15px]"
                    />
                  )}
                </FormField>
                {d.kind === 'form' && (
                  <FormField label="Which form">
                    {(p) => (
                      <NativeSelect
                        {...p}
                        value={d.form ?? 'starter_details'}
                        onChange={(e) =>
                          set({ form: e.target.value as 'starter_details' | 'uniform' })
                        }
                      >
                        <option value="starter_details">
                          Contact and emergency details, driving licence, health (optional)
                        </option>
                        <option value="uniform">Uniform sizes</option>
                      </NativeSelect>
                    )}
                  </FormField>
                )}
                {(d.kind === 'sign' || d.kind === 'acknowledge') && (
                  <FormField
                    label="Document to read"
                    hint="From the Library. The starter opens it before confirming or signing."
                  >
                    {(p) => (
                      <NativeSelect
                        {...p}
                        value={d.documentId ?? ''}
                        onChange={(e) => set({ documentId: e.target.value || null })}
                      >
                        <option value="">None: the instructions above are enough</option>
                        {docs.map((doc) => (
                          <option key={doc.id} value={doc.id}>
                            {doc.title}
                          </option>
                        ))}
                      </NativeSelect>
                    )}
                  </FormField>
                )}
                <ToggleRow
                  label="Required"
                  description="The pack cannot be completed without it."
                  checked={!!d.required}
                  onChange={(v) => set({ required: v })}
                />
              </>
            ),
          }
        }}
      />
    </>
  )
}

// ---- Proposal content ---------------------------------------------------
const MERGE_FIELDS = [
  'date',
  'client_name',
  'client_company',
  'client_address',
  'client_business',
  'hcd_legal_name',
  'hcd_company_number',
  'hcd_address',
]

export function ProposalContentPage({ me }: { me: StaffUser }) {
  return (
    <>
      <PageTitle
        title="Proposal content"
        description="The standard wording on every proposal. Each quote copies this when it is signed and sent, so changes only affect new proposals."
      />
      <SectionEditor section="proposal" canEdit={can.manageSettings(me)}>
        {({ draft, set, errors, disabled }) => (
          <div className="grid gap-5">
            <SettingsCard
              title="About us"
              description='Start a line with "# " to make it a heading. Leave a blank line between paragraphs.'
            >
              <FormField label="About us and our services" error={errors.aboutUs}>
                {(p) => (
                  <Textarea
                    {...p}
                    rows={16}
                    value={draft.aboutUs}
                    disabled={disabled}
                    onChange={(e) => set('aboutUs', e.target.value)}
                    className="font-mono text-sm"
                  />
                )}
              </FormField>
            </SettingsCard>
            <SettingsCard
              title="Services and sectors"
              description="Shown after the prices. One per line."
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <FormField label="Scope heading" error={errors.scopeHeading}>
                  {(p) => (
                    <TextInput
                      {...p}
                      value={draft.scopeHeading}
                      disabled={disabled}
                      onChange={(e) => set('scopeHeading', e.target.value)}
                    />
                  )}
                </FormField>
                <FormField label="Other services heading">
                  {(p) => (
                    <TextInput
                      {...p}
                      value={draft.otherServicesHeading}
                      disabled={disabled}
                      onChange={(e) => set('otherServicesHeading', e.target.value)}
                    />
                  )}
                </FormField>
                <FormField
                  label="Other services we offer"
                  error={errors.otherServices && 'Each line can be up to 80 characters.'}
                >
                  {(p) => (
                    <Textarea
                      {...p}
                      rows={8}
                      value={draft.otherServices.join('\n')}
                      disabled={disabled}
                      onChange={(e) => set('otherServices', lines(e.target.value))}
                      className="text-base md:text-[15px]"
                    />
                  )}
                </FormField>
                <FormField
                  label="Sectors we specialise in"
                  error={errors.sectors && 'Each line can be up to 80 characters.'}
                >
                  {(p) => (
                    <Textarea
                      {...p}
                      rows={8}
                      value={draft.sectors.join('\n')}
                      disabled={disabled}
                      onChange={(e) => set('sectors', lines(e.target.value))}
                      className="text-base md:text-[15px]"
                    />
                  )}
                </FormField>
              </div>
            </SettingsCard>
            <SettingsCard
              title="Agreement"
              description="The wording the client signs. The client's details are filled in when they sign."
            >
              <FormField label="Agreement text" error={errors.agreementText}>
                {(p) => (
                  <Textarea
                    {...p}
                    rows={14}
                    value={draft.agreementText}
                    disabled={disabled}
                    onChange={(e) => set('agreementText', e.target.value)}
                    className="font-mono text-sm"
                  />
                )}
              </FormField>
              <div>
                <p className="mb-2 text-sm font-medium">Fields you can use</p>
                <ul className="flex flex-wrap gap-2">
                  {MERGE_FIELDS.map((f) => (
                    <li key={f}>
                      <code className="rounded-md border bg-muted px-2 py-1 text-[13px]">{`{{${f}}}`}</code>
                    </li>
                  ))}
                </ul>
              </div>
              <FormField label="Declaration the client ticks" error={errors.declarationText}>
                {(p) => (
                  <TextInput
                    {...p}
                    value={draft.declarationText}
                    disabled={disabled}
                    onChange={(e) => set('declarationText', e.target.value)}
                  />
                )}
              </FormField>
              <FormField label="Note above the sign button" error={errors.acceptanceNote}>
                {(p) => (
                  <Textarea
                    {...p}
                    rows={2}
                    value={draft.acceptanceNote}
                    disabled={disabled}
                    onChange={(e) => set('acceptanceNote', e.target.value)}
                    className="text-base md:text-[15px]"
                  />
                )}
              </FormField>
            </SettingsCard>
          </div>
        )}
      </SectionEditor>
    </>
  )
}

const lines = (v: string) =>
  v
    .split('\n')
    .map((l) => l.trimStart())
    .filter((l, i, all) => l.length > 0 || i === all.length - 1)
